export function toHHmm(decHours) {
    if (decHours == null || isNaN(decHours)) return "-";
    const sign = decHours < 0 ? "-" : "";
    const abs = Math.abs(decHours);
    const h = Math.floor(abs);
    const m = Math.round((abs - h) * 60);
    return `${sign}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Nombre de empresa para mostrar. El claim `company` del JWT es el slug del
 * tenant en minuscula ("lideres"), y se ve en el pill del topbar de todos los
 * portales. Solo levantamos la primera letra: si el nombre ya viene con
 * mayusculas propias ("SOFTNIK SA", "Grupo Lideres") queda intacto.
 */
export function formatCompanyName(company) {
    if (!company) return "";
    return company.charAt(0).toUpperCase() + company.slice(1);
}

// ── Montos y fechas (Portal de Legales, reutilizables) ─────────────
// Todas devuelven "—" cuando no hay dato, así una celda vacía no se confunde
// con un cero. Las fechas del backend llegan como "YYYY-MM-DD" y se formatean
// cortando el string: pasar por new Date() las corre un día en Argentina.

const SIN_DATO = "—";

function esNumero(n) {
    return n !== null && n !== undefined && n !== "" && !Number.isNaN(Number(n));
}

function formatMoneda(n, currency, decimales) {
    if (!esNumero(n)) return SIN_DATO;
    const valor = Number(n);
    const dec = decimales ?? (Number.isInteger(valor) ? 0 : 2);
    return new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency,
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
    }).format(valor);
}

/** $ 1.234.567 (con centavos solo si el monto los tiene, o `decimales` fijo). */
export function formatARS(n, { decimales } = {}) {
    return formatMoneda(n, "ARS", decimales);
}

/** US$ 1.234 (con centavos solo si el monto los tiene, o `decimales` fijo). */
export function formatUSD(n, { decimales } = {}) {
    return formatMoneda(n, "USD", decimales);
}

/** 1.234.567 */
export function formatNumero(n, { decimales = 0 } = {}) {
    if (!esNumero(n)) return SIN_DATO;
    return new Intl.NumberFormat("es-AR", {
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales,
    }).format(Number(n));
}

function partesFecha(fecha) {
    if (!fecha) return null;
    if (fecha instanceof Date) {
        if (Number.isNaN(fecha.getTime())) return null;
        return [fecha.getFullYear(), fecha.getMonth() + 1, fecha.getDate()];
    }
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(fecha));
    if (!m) return null;
    return [Number(m[1]), Number(m[2]), Number(m[3])];
}

const dos = (n) => String(n).padStart(2, "0");

/** "2026-09-24" → "24/09/2026". Acepta también un Date o un ISO con hora. */
export function formatFecha(fecha) {
    const p = partesFecha(fecha);
    if (!p) return SIN_DATO;
    return `${dos(p[2])}/${dos(p[1])}/${p[0]}`;
}

/** "2026-09-24" → "24/09/26". */
export function formatFechaCorta(fecha) {
    const p = partesFecha(fecha);
    if (!p) return SIN_DATO;
    return `${dos(p[2])}/${dos(p[1])}/${String(p[0]).slice(-2)}`;
}

/** "2026-09-24T14:05:00" → "24/09/2026 14:05" (la hora tal cual llega). */
export function formatFechaHora(valor) {
    if (!valor) return SIN_DATO;
    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(valor));
    if (!m) return formatFecha(valor);
    return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
}

/**
 * Días desde hoy hasta `fecha` ("YYYY-MM-DD"): 0 = hoy, negativo = ya pasó.
 * `hoy` es opcional (Date o "YYYY-MM-DD"), para tests. Devuelve null sin fecha.
 */
export function diasHasta(fecha, hoy = new Date()) {
    const f = partesFecha(fecha);
    const h = partesFecha(hoy);
    if (!f || !h) return null;
    const a = Date.UTC(h[0], h[1] - 1, h[2]);
    const b = Date.UTC(f[0], f[1] - 1, f[2]);
    return Math.round((b - a) / 86400000);
}

/**
 * Texto corto para un plazo: 0 → "Hoy", 1 → "Mañana", -1 → "Ayer",
 * 5 → "En 5 días", -3 → "Hace 3 días". null → "—".
 */
export function formatDiasRelativos(dias) {
    if (dias === null || dias === undefined || Number.isNaN(Number(dias))) return SIN_DATO;
    const n = Number(dias);
    if (n === 0) return "Hoy";
    if (n === 1) return "Mañana";
    if (n === -1) return "Ayer";
    return n > 0 ? `En ${n} días` : `Hace ${-n} días`;
}

const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/** "2026-09" o "2026-09-24" → "sep 2026" (`{ corto: true }` → "sep 26"), para ejes y títulos. */
export function formatMes(mes, { corto = false } = {}) {
    const m = /^(\d{4})-(\d{2})/.exec(String(mes || ""));
    if (!m) return SIN_DATO;
    const nombre = MESES_CORTOS[Number(m[2]) - 1];
    if (!nombre) return SIN_DATO;
    return `${nombre} ${corto ? m[1].slice(-2) : m[1]}`;
}

/** $ 2,6 M / $ 850 mil / $ 900, para ejes de gráficos y KPI chicos. */
export function formatARSCompacto(n) {
    if (!esNumero(n)) return SIN_DATO;
    const v = Number(n);
    const abs = Math.abs(v);
    const num = (x, dec) => new Intl.NumberFormat("es-AR", { maximumFractionDigits: dec }).format(x);
    if (abs >= 1e9) return `$ ${num(v / 1e9, 1)} mil M`;
    if (abs >= 1e6) return `$ ${num(v / 1e6, 1)} M`;
    if (abs >= 1e3) return `$ ${num(v / 1e3, 0)} mil`;
    return `$ ${num(v, 0)}`;
}
